import React, { useEffect, useRef, useState } from "react";
import PrimaryButton from "../../../shared/ui/buttons/PrimaryButton";
import { useOperator } from "../../../../utils/OperatorContext";

const ACTION_ID_SHOW_DESCRIPTION = "SHOW_DESCRIPTION";
const ACTION_ID_CHALLENGES = "SHOW_CHALLENGES";
const ACTION_ID_SHOW_HEARTBEAT = "SHOW_HEARTBEAT";
const ACTION_ID_SHOW_TIMER = "SHOW_TIMER";
const ACTION_ID_TOGGLE_MINI_TIMER = "TOGGLE_MINI_TIMER";

// broadcast selected day = GLOBAL
const ACTION_ID_SET_SELECTED_DAY = "WALL_SET_SELECTED_DAY";
const SELECTED_DAY_KEY = "wall:GLOBAL";

const LS_KEY_TIMER_STATE = "wall:timer_state";
const IS_LOOP_EVENT = "";

const fire = (dispatchAction, bc, actionId, payload = {}) => {
  dispatchAction(actionId, payload);
  if (bc?.current) bc.current.postMessage({ actionId, payload });
};

export default function WallTimerControls() {
  const { dispatchAction } = useOperator();
  const bcRef = useRef(null);
  const pendingTimerTimeout = useRef(null);

  const [title, setTitle] = useState("");
  const [seconds, setSeconds] = useState("");
  const [reverse, setReverse] = useState(false);

  const [challengeTitle, setChallengeTitle] = useState("");
  const [challengeDescription, setChallengeDescription] = useState("");

  const [enableHeartbeat, setEnableHeartbeat] = useState(false);
  const [countFrom, setCountFrom] = useState(3); // default 3s
  const [autoStartTimer, setAutoStartTimer] = useState(false);
  const [heartbeatDelaySec, setHeartbeatDelaySec] = useState(2);

  // Keep HeartBeat overlay visible after counting, if desired
  const [stayHeartbeatVisible, setStayHeartbeatVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    bcRef.current = new BroadcastChannel("operator-events");
    return () => {
      if (bcRef.current) bcRef.current.close();
      if (pendingTimerTimeout.current) clearTimeout(pendingTimerTimeout.current);
    };
  }, []);

  // ensure GLOBAL is selected (localStorage + broadcast)
  const ensureGlobalSelected = () => {
    try {
      localStorage.setItem(SELECTED_DAY_KEY, "GLOBAL");
    } catch {}
    fire(dispatchAction, bcRef, ACTION_ID_SET_SELECTED_DAY, { selected: "GLOBAL" });
  };

  const buildTimerPayload = (extra = {}) => {
    const duration = parseInt(seconds, 10);
    if (isNaN(duration) || duration <= 0) {
      alert("مدت‌زمان معتبر وارد کنید");
      return null;
    }
    return {
      title: title?.trim() || null,
      duration,
      reverse: !!reverse,
      ...extra,
    };
  };

  const sendIsLoop = (isLoop) => {
    localStorage.setItem(IS_LOOP_EVENT, JSON.stringify({ isLoop }));
  };

  const sendTimerStart = () => {
    ensureGlobalSelected();
    const payload = buildTimerPayload({ mode: "start", startedAt: Date.now() });
    if (!payload) return;
    localStorage.setItem(LS_KEY_TIMER_STATE, JSON.stringify(payload));
    fire(dispatchAction, bcRef, ACTION_ID_SHOW_TIMER, payload);
  };

  const sendTimerPrepare = () => {
    ensureGlobalSelected();
    const payload = buildTimerPayload({
      mode: "prepare",
      startedAt: null,
      paused: true,
    });
    if (!payload) return;
    localStorage.setItem(LS_KEY_TIMER_STATE, JSON.stringify(payload));
    fire(dispatchAction, bcRef, ACTION_ID_SHOW_TIMER, payload);
  };

  const startPrepared = () => {
    ensureGlobalSelected();
    let prepared = null;
    try {
      prepared = JSON.parse(localStorage.getItem(LS_KEY_TIMER_STATE));
    } catch {}
    const base =
      prepared && prepared.duration ? prepared : buildTimerPayload({});
    if (!base) return;
    const payload = { ...base, mode: "start", startedAt: Date.now() };
    localStorage.setItem(LS_KEY_TIMER_STATE, JSON.stringify(payload));
    fire(dispatchAction, bcRef, ACTION_ID_SHOW_TIMER, payload);
  };

  const resetTimer = () => {
    ensureGlobalSelected();
    localStorage.removeItem(LS_KEY_TIMER_STATE);
    fire(dispatchAction, bcRef, ACTION_ID_SHOW_TIMER, { mode: "reset" });
  };

  const toggleMiniTimer = (visible) => {
    ensureGlobalSelected();
    fire(dispatchAction, bcRef, ACTION_ID_TOGGLE_MINI_TIMER, { visible });
  };

  const showDescriptionOnly = () => {
    ensureGlobalSelected();
    const t = (challengeTitle ?? "").trim();
    const d = (challengeDescription ?? "").trim();
    fire(dispatchAction, bcRef, ACTION_ID_SHOW_DESCRIPTION, {});
    setTimeout(
      () =>
        fire(dispatchAction, bcRef, ACTION_ID_CHALLENGES, {
          title: t,
          description: d,
        }),
      250
    );
  };

  // HeartBeat trigger: defaults to 3 seconds if empty/invalid
  const triggerHeartbeatAndAutoStart = () => {
    ensureGlobalSelected();
    if (pendingTimerTimeout.current) clearTimeout(pendingTimerTimeout.current);
    const count = Math.max(0, parseInt(countFrom, 10) || 3);

    fire(dispatchAction, bcRef, ACTION_ID_SHOW_HEARTBEAT, {
      countFrom: count,
      autoStartTimer,
      title: title?.trim() || null,
      timerDuration: parseInt(seconds, 10) || 0,
      reverse: !!reverse,
      // disappear after finish? (true => overlay disappears)
      disapear: !stayHeartbeatVisible,
    });

    // Align auto-start with the countdown
    const ms = (count + 0.2) * 1000;
    pendingTimerTimeout.current = setTimeout(() => sendTimerStart(), ms);
  };

  const showHeartbeatOnly = () => triggerHeartbeatAndAutoStart();
  const showTimerOnly = () => sendTimerStart();

  const runConfiguredFlow = () => {
    ensureGlobalSelected();
    const t = (challengeTitle ?? "").trim();
    const d = (challengeDescription ?? "").trim();
    if (!t && !d) {
      alert("عنوان یا توضیح چالش را وارد کنید");
      return;
    }
    fire(dispatchAction, bcRef, ACTION_ID_SHOW_DESCRIPTION, {});
    setTimeout(
      () =>
        fire(dispatchAction, bcRef, ACTION_ID_CHALLENGES, {
          title: t,
          description: d,
        }),
      300
    );
    if (enableHeartbeat) {
      const delayMs = Math.max(
        0,
        (parseInt(heartbeatDelaySec, 10) || 0) * 1000
      );
      setTimeout(() => triggerHeartbeatAndAutoStart(), delayMs);
    }
  };

  const setPreset = (sec) => setSeconds(String(sec));

  // UI helpers — all set to 40px
  const Section = ({ title, children }) => (
    <div className="rounded-2xl border border-border/60 bg-card/70 backdrop-blur px-6 py-6 space-y-5 text-[40px]">
      {title ? (
        <h3 className="text-[40px] font-semibold leading-tight text-white">
          {title}
        </h3>
      ) : null}
      {children}
    </div>
  );

  const Label = ({ children }) => (
    <label className="text-[40px] font-medium text-white/90">
      {children}
    </label>
  );

  const inputBase =
    "rounded-2xl border border-white/20 bg-black/30 focus:bg-black/40 outline-none px-5 py-4 text-[40px] text-white placeholder-white/60";

  const checkboxBase = "accent-primary w-8 h-8"; // larger checkbox

  return (
    <div className="space-y-6 text-[40px]">
      {/* Header / Mini-timer toggles */}
      <Section>
        <div className="flex items-center justify-between gap-6">
          <h4 className="text-[40px] font-bold text-white">
            ارسال تایمر به وال
          </h4>
          <div className="flex flex-wrap gap-3">
            <PrimaryButton
              text="نمایش مینی تایمر"
              onClick={() => toggleMiniTimer(true)}
              className="bg-primary text-card text-[40px] px-6 py-4 rounded-2xl"
            />
            <PrimaryButton
              text="مخفی‌سازی مینی تایمر"
              onClick={() => toggleMiniTimer(false)}
              className="bg-red-600 text-card text-[40px] px-6 py-4 rounded-2xl"
            />
          </div>
        </div>
      </Section>

      {/* Timer settings */}
      <Section title="تنظیمات تایمر">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div className="flex flex-col gap-3">
            <Label>عنوان تایمر</Label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="مثلاً: شمارش معکوس"
              className={inputBase}
            />
          </div>

          <div className="flex flex-col gap-3">
            <Label>مدت‌زمان (ثانیه)</Label>
            <input
              type="number"
              min="1"
              value={seconds}
              onChange={(e) => setSeconds(e.target.value)}
              placeholder="مثلاً 60"
              className={inputBase}
            />
          </div>

          <div className="flex items-end">
            <label className="inline-flex items-center gap-4 text-white/90">
              <input
                type="checkbox"
                className={checkboxBase}
                checked={reverse}
                onChange={(e) => setReverse(e.target.checked)}
              />
              تایمر معکوس باشد
            </label>
          </div>

          <div className="flex items-end">
            <div className="flex flex-wrap gap-3">
              {[
                ["۱m", 60],
                ["۵m", 300],
                ["۱۰m", 600],
                ["۱۵m", 900],
                ["۳۰m", 1800],
                ["۴۵m", 2700],
                ["۱h", 3600],
              ].map(([label, value]) => (
                <button
                  key={label}
                  onClick={() => setPreset(value)}
                  className="rounded-2xl px-4 py-3 text-[40px] bg-black/30 border border-white/15 text-white hover:bg-black/40 transition"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Challenge copy */}
      <Section title="متن چالش">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="flex flex-col gap-3 md:col-span-1">
            <Label>عنوان چالش</Label>
            <input
              value={challengeTitle}
              onChange={(e) => setChallengeTitle(e.target.value)}
              placeholder="مثلاً: فینال بزرگ"
              className={inputBase}
            />
          </div>
          <div className="flex flex-col gap-3 md:col-span-2">
            <Label>توضیحات چالش</Label>
            <textarea
              rows={3}
              value={challengeDescription}
              onChange={(e) => setChallengeDescription(e.target.value)}
              placeholder="توضیحات..."
              className={`${inputBase} resize-none`}
            />
          </div>
        </div>
      </Section>

      {/* HeartBeat settings */}
      <Section title="HeartBeat">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
          <label className="flex items-center gap-4 text-white/90">
            <input
              type="checkbox"
              className={checkboxBase}
              checked={enableHeartbeat}
              onChange={(e) => setEnableHeartbeat(e.target.checked)}
            />
            نمایش HeartBeat
          </label>

          <div className="flex items-center gap-4">
            <span className="text-white/90">شمارش از</span>
            <input
              type="number"
              min="0"
              value={countFrom}
              onChange={(e) => setCountFrom(e.target.value)}
              className={`${inputBase} w-40 text-center`}
            />
            <span className="text-white/70">(پیش‌فرض ۳)</span>
          </div>

          <label className="flex items-center gap-4 text-white/90">
            <input
              type="checkbox"
              className={checkboxBase}
              checked={autoStartTimer}
              onChange={(e) => setAutoStartTimer(e.target.checked)}
            />
            شروع خودکار تایمر بعد از HeartBeat
          </label>

          <div className="flex items-center gap-4">
            <span className="text-white/90">
              تاخیر بین توضیح → HeartBeat (ثانیه)
            </span>
            <input
              type="number"
              min="0"
              value={heartbeatDelaySec}
              onChange={(e) => setHeartbeatDelaySec(e.target.value)}
              className={`${inputBase} w-44 text-center`}
            />
          </div>
        </div>

        <div className="pt-3">
          <label className="flex items-center gap-4 text-white/90">
            <input
              type="checkbox"
              className={checkboxBase}
              checked={stayHeartbeatVisible}
              onChange={(e) => setStayHeartbeatVisible(e.target.checked)}
            />
            HeartBeat روی صفحه باقی بماند
          </label>
        </div>
      </Section>

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section title="کنترل‌های دستی">
          <div className="flex flex-wrap gap-4">
            <PrimaryButton
              text="نمایش توضیح چالش"
              onClick={showDescriptionOnly}
              className="bg-indigo-600 text-card text-[40px] px-6 py-4 rounded-2xl"
            />
            <PrimaryButton
              text="نمایش HeartBeat"
              onClick={showHeartbeatOnly}
              className="bg-pink-600 text-card text-[40px] px-6 py-4 rounded-2xl"
            />
            <PrimaryButton
              text="ارسال + شروع تایمر"
              onClick={showTimerOnly}
              className="bg-emerald-600 text-card text-[40px] px-6 py-4 rounded-2xl"
            />
            <PrimaryButton
              text="فقط ارسال تایمر"
              onClick={sendTimerPrepare}
              className="bg-amber-600 text-card text-[40px] px-6 py-4 rounded-2xl"
            />
            <PrimaryButton
              text="شروع شمارش آماده"
              onClick={startPrepared}
              className="bg-blue-600 text-card text-[40px] px-6 py-4 rounded-2xl"
            />
            <PrimaryButton
              text="ریست تایمر"
              onClick={resetTimer}
              className="bg-red-600 text-card text-[40px] px-6 py-4 rounded-2xl"
            />
          </div>
        </Section>

        <Section title="اجرای خودکار با تنظیمات بالا">
          <div className="flex flex-wrap gap-4">
            <PrimaryButton
              text="اجرای فلو چالش"
              onClick={runConfiguredFlow}
              className="bg-primary text-card text-[40px] px-6 py-4 rounded-2xl"
            />
          </div>
        </Section>
      </div>
    </div>
  );
}
